import React, { useEffect, useState } from "react";
import { FileStack } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({ source_type: "supplier", source_id: "", source_name: "", bill_date: today(), reference_no: "", total_qty: "", total_gold_weight_g: "", total_diamond_ct: "", total_inr_cost: "", notes: "" });

// Record a Supplier / Karigar Master Bill (or an Opening Balance batch) as TOTALS.
// This only creates the Pending Stock Pool - stock rises when pieces are itemized.
export function NewMasterBillDialog({ open, onOpenChange, suppliers, karigars, onCreated }) {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(empty()); }, [open]);

  const entities = form.source_type === "supplier" ? suppliers : form.source_type === "karigar" ? karigars : [];

  const save = async () => {
    if (!(Number(form.total_qty) > 0)) { toast.error("Total quantity must be at least 1"); return; }
    if (form.source_type !== "opening" && !form.source_id && !form.source_name.trim()) { toast.error(`Select a ${form.source_type} or type a name`); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_qty: Number(form.total_qty),
        total_gold_weight_g: Number(form.total_gold_weight_g) || 0,
        total_diamond_ct: Number(form.total_diamond_ct) || 0,
        total_inr_cost: Number(form.total_inr_cost) || 0,
      };
      const { data } = await api.post("/inventory/master-bills", payload);
      toast.success(`Master Bill ${data.bill.bill_no} recorded`, { description: `${data.bill.total_qty} piece(s) waiting to be itemized` });
      onCreated?.(data.bill);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save bill"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-master-bill-dialog" className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><FileStack className="h-5 w-5 text-[hsl(var(--primary))]" /> New Master Bill</DialogTitle>
          <DialogDescription>Enter the bill's totals only. This opens a Pending Stock Pool - the vault count rises when you itemize each physical piece from it.</DialogDescription>
        </DialogHeader>

        <Tabs value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v, source_id: "", source_name: v === "opening" ? "Opening Balance / Self" : "" })}>
          <TabsList className="grid grid-cols-3 w-full" data-testid="bill-source-tabs">
            <TabsTrigger data-testid="bill-source-supplier" value="supplier">Supplier</TabsTrigger>
            <TabsTrigger data-testid="bill-source-karigar" value="karigar">Karigar</TabsTrigger>
            <TabsTrigger data-testid="bill-source-opening" value="opening">Opening Balance</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {form.source_type !== "opening" ? (
            <>
              <div className="min-w-0">
                <Label className="text-xs">{form.source_type === "supplier" ? "Supplier" : "Karigar"}</Label>
                <Select value={form.source_id || "manual"} onValueChange={(v) => setForm({ ...form, source_id: v === "manual" ? "" : v })}>
                  <SelectTrigger data-testid="bill-source-select" className="mt-1 h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Type a name instead...</SelectItem>
                    {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0">
                <Label className="text-xs">Name (if not in the list)</Label>
                <Input data-testid="bill-source-name" value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} disabled={!!form.source_id} placeholder="e.g. Shree Gold Works" className="mt-1 h-10" />
              </div>
            </>
          ) : (
            <div className="sm:col-span-2 rounded-lg border border-border bg-[#F7F0E6] p-3 text-xs text-muted-foreground">Source will be recorded as <span className="font-medium text-foreground">Opening Balance / Self</span>. Use this for stock you already own at go-live.</div>
          )}
          <div>
            <Label className="text-xs">Bill date</Label>
            <Input data-testid="bill-date" type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} className="mt-1 h-10" />
          </div>
          <div>
            <Label className="text-xs">Supplier / Karigar bill no.</Label>
            <Input data-testid="bill-reference" value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} placeholder="e.g. INV-2214" className="mt-1 h-10 font-mono" />
          </div>
        </div>

        <div className="rounded-xl border border-border p-4 bg-white/60">
          <div className="text-sm font-medium mb-3">Bill totals</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Total pieces</Label><Input data-testid="bill-total-qty" type="number" min="1" step="1" value={form.total_qty} onChange={(e) => setForm({ ...form, total_qty: e.target.value })} className="mt-1 h-10 font-mono" /></div>
            <div><Label className="text-xs">Gold weight (g)</Label><Input data-testid="bill-total-gold" type="number" min="0" step="0.001" value={form.total_gold_weight_g} onChange={(e) => setForm({ ...form, total_gold_weight_g: e.target.value })} className="mt-1 h-10 font-mono" /></div>
            <div><Label className="text-xs">Diamonds (ct)</Label><Input data-testid="bill-total-diamond" type="number" min="0" step="0.01" value={form.total_diamond_ct} onChange={(e) => setForm({ ...form, total_diamond_ct: e.target.value })} className="mt-1 h-10 font-mono" /></div>
            <div><Label className="text-xs">Total cost (INR)</Label><Input data-testid="bill-total-inr" type="number" min="0" step="1" value={form.total_inr_cost} onChange={(e) => setForm({ ...form, total_inr_cost: e.target.value })} className="mt-1 h-10 font-mono" /></div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Cost is allocated to each piece in proportion to its gross weight (override per piece while itemizing). Weight and carats are hard limits - you can never itemize more than the bill.</p>
        </div>

        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea data-testid="bill-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" placeholder="Optional" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-master-bill-button" onClick={save} disabled={saving}>{saving ? "Saving..." : "Record Master Bill"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
